import { Request } from './Request'
import { offlineRiddleStore, RiddleLocale, RiddleSnapshot } from './OfflineRiddleStore'

interface SoloRiddleWord {
  id: number
  ru: string
  eng: string
}

interface SoloRiddle {
  id: number
  attemptId?: string
  clue: string
  words: SoloRiddleWord[]
  targetCount: 2 | 3
  targetPositions: number[]
  total?: number
}

interface LegacySoloRiddleMode {
  mode: 'legacy'
}

interface ExhaustedSoloRiddleMode {
  mode: 'exhausted'
}

interface LocalSoloRiddle extends SoloRiddle {
  mode: 'local'
  total: number
}

interface DynamicSoloRiddle extends SoloRiddle {
  mode: 'dynamic'
  attemptId: string
  total: number
}

type SoloDifficulty = 'novice' | 'experienced' | 'master'

interface RiddleSnapshotResponse {
  version: string
  riddles: SoloRiddle[]
}

interface QueuedRun {
  runId: string
  userId: string
  playerName: string
  difficulty: SoloDifficulty
  score: number
  results: SoloRunResult[]
  durationSeconds?: number
}

interface SoloRunResult {
  riddleId: number
  correct: boolean
  locale: RiddleLocale
}

const RUN_QUEUE_KEY = 'solo-run-sync:v1'
const SNAPSHOT_REFRESH_INTERVAL = 5 * 60 * 1000

class SoloRiddleService {
  private static refreshes: Partial<Record<RiddleLocale, Promise<RiddleSnapshot | null>>> = {}
  private static flushing = false

  private chooseRiddle(snapshot: RiddleSnapshot, excludeIds: number[], onlyTwoWords: boolean) {
    const available = snapshot.riddles.filter(riddle =>
      !excludeIds.includes(riddle.id) && (!onlyTwoWords || riddle.targetCount === 2)
    )

    if (!available.length) {
      return { status: true, data: { mode: 'exhausted' } as ExhaustedSoloRiddleMode }
    }

    const riddle = available[Math.floor(Math.random() * available.length)]
    return {
      status: true,
      data: { ...riddle, mode: 'local', total: snapshot.riddles.length } as LocalSoloRiddle
    }
  }

  private async refreshSnapshot(locale: RiddleLocale, force = false) {
    const cached = await offlineRiddleStore.get(locale)
    if (!force && cached && Date.now() - cached.updatedAt < SNAPSHOT_REFRESH_INTERVAL) return cached

    const currentRefresh = SoloRiddleService.refreshes[locale]
    if (currentRefresh) return currentRefresh

    const refresh = (async () => {
      const response = await Request.create<RiddleSnapshotResponse>({
        url: '/solo-riddles',
        params: { locale }
      })
      if (!response?.data || !Array.isArray(response.data.riddles) || !response.data.riddles.length) return cached

      const snapshot: RiddleSnapshot = {
        locale,
        version: response.data.version,
        updatedAt: Date.now(),
        riddles: response.data.riddles
      }
      await offlineRiddleStore.set(snapshot)
      return snapshot
    })().finally(() => {
      delete SoloRiddleService.refreshes[locale]
    })

    SoloRiddleService.refreshes[locale] = refresh
    return refresh
  }

  async getRandom(locale: 'ru' | 'en', excludeIds: number[] = [], onlyTwoWords = false) {
    const cached = await offlineRiddleStore.get(locale)
    if (cached?.riddles.length) {
      void this.refreshSnapshot(locale)
      return this.chooseRiddle(cached, excludeIds, onlyTwoWords)
    }

    const snapshot = await this.refreshSnapshot(locale, true)
    if (snapshot?.riddles.length) return this.chooseRiddle(snapshot, excludeIds, onlyTwoWords)

    return { status: true, data: { mode: 'legacy' } as LegacySoloRiddleMode }
  }

  async getAvailableCount(locale: RiddleLocale) {
    const cached = await offlineRiddleStore.get(locale)
    if (cached?.riddles.length) {
      const refreshed = await this.refreshSnapshot(locale)
      return refreshed?.riddles.length || cached.riddles.length
    }

    const snapshot = await this.refreshSnapshot(locale, true)
    return snapshot?.riddles.length || 0
  }

  async recordResult(riddleId: number, attemptId: string, correct: boolean) {
    return await Request.create<{ recorded: boolean }>({
      url: `/solo-riddle/${riddleId}/result`,
      method: 'post',
      data: { attemptId, correct }
    })
  }

  async recordRun(runId: string, userId: string, playerName: string, difficulty: SoloDifficulty, score: number, results: SoloRunResult[], durationSeconds: number) {
    this.queueRun({ runId, userId, playerName, difficulty, score, results, durationSeconds })
    void this.flushRuns()
    return { status: true, data: { recorded: navigator.onLine } }
  }

  async preload() {
    if (navigator.storage?.persist) void navigator.storage.persist()
    await Promise.all([this.refreshSnapshot('ru'), this.refreshSnapshot('en')])
    await this.flushRuns()
  }

  private readRunQueue(): QueuedRun[] {
    try {
      return JSON.parse(localStorage.getItem(RUN_QUEUE_KEY) || '[]') as QueuedRun[]
    }
    catch {
      return []
    }
  }

  private writeRunQueue(queue: QueuedRun[]) {
    localStorage.setItem(RUN_QUEUE_KEY, JSON.stringify(queue))
  }

  private queueRun(run: QueuedRun) {
    const queue = this.readRunQueue()
    const existing = queue.find(item => item.runId === run.runId)
    if (existing) {
      existing.playerName = run.playerName
      existing.score = run.score
      existing.results = run.results
      existing.durationSeconds = run.durationSeconds
    }
    else {
      queue.push(run)
    }
    this.writeRunQueue(queue)
  }

  async flushRuns() {
    if (SoloRiddleService.flushing || !navigator.onLine) return
    SoloRiddleService.flushing = true

    try {
      while (navigator.onLine) {
        const run = this.readRunQueue()[0]
        if (!run) break

        const response = await Request.create<{ recorded: boolean }>({
          url: '/solo-run',
          method: 'post',
          data: run
        })
        if (!response?.status) break

        // A final score may replace the queued zero-score record while this
        // request is in flight. Remove only the version we actually sent.
        const latestQueue = this.readRunQueue().filter(item =>
          item.runId !== run.runId || item.score > run.score
        )
        this.writeRunQueue(latestQueue)
      }
    }
    finally {
      SoloRiddleService.flushing = false
    }
  }
}

export { DynamicSoloRiddle, ExhaustedSoloRiddleMode, LegacySoloRiddleMode, LocalSoloRiddle, SoloDifficulty, SoloRiddle, SoloRiddleService }
